import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
  Avatar,
  Button,
} from '@mui/material'
import moment from 'moment'
import API from '../../../services/API'
import { riskFlagOptions } from '../NewPatient/clinicalRiskFlags'
import MFALogin from '../../../helpers/auth/MFALogin'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import AddNewPatient from '../NewPatient/AddNewPatient'
import LogsComponent from '../../../components/Logs/LogsComponent'
import { DetailRow } from '../../../components/Cards/DetailsBox'
import ExistingJourneys from '../NewBooking/ExistingJourneys'
import InfoCard from '../../../components/Cards/InfoCard'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import { isIrishRegion } from '../../../helpers/region'
import RiskAssessments from './RiskAssessments'

const PTSPatient = () => {
  const { patientID } = useParams()
  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const navigate = useNavigate()
  const [logsLoadedAt, setLogsLoadedAt] = useState(moment().toDate())
  const { tenantInfo } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig

  useEffect(() => {
    if (!patientID) return
    setLoading(true)
    API.get(`/pts/patient/${patientID}`)
      .then((res) => {
        if (res.status === 200) {
          setPatient(res.data)
        } else if (res.data?.errorCode === 'notAuthorised') {
          setRequiresMFA(true)
        } else {
          setError('Unable to load patient record.')
        }
      })
      .catch(() => setError('An error occurred while fetching patient data.'))
      .finally(() => setLoading(false))
  }, [patientID, requiresMFA])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <CircularProgress color="anPurple" />
      </Box>
    )

  if (requiresMFA) return <MFALogin onSuccess={() => setRequiresMFA(false)} />

  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Box>
    )

  if (!patient)
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No patient record found.
        </Typography>
      </Box>
    )

  const {
    demographics = {},
    mobility = {},
    riskFlags = [],
    clinicalNotes,
    allergies = [],
    medications = [],
    pmhx = [],
  } = patient

  const categorisedRisks = riskFlagOptions
    .map(({ group, color, options }) => ({
      group,
      color,
      flags: riskFlags.filter((flag) => options.includes(flag)),
    }))
    .filter((cat) => cat.flags.length > 0)

  const age = demographics.dob
    ? moment().diff(moment(demographics.dob), 'years')
    : '—'

  return (
    <Box sx={{ p: 3 }}>
      {/* HEADER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          p: 3,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #f9f9fb 0%, #eef1f8 100%)',
          boxShadow: '0 4px 18px rgba(0,0,0,0.05)',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction="row" spacing={2}>
            <Stack>
              <Avatar
                sx={{
                  bgcolor: 'contrast.main',
                  width: 64,
                  height: 64,
                  fontSize: 26,
                  fontWeight: 600,
                  borderRadius: 1,
                }}
              >
                {demographics.firstName?.[0]}
                {demographics.lastName?.[0]}
              </Avatar>
              <Box mt={0.4}>
                <Button
                  size="small"
                  color="anPurple"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </Box>
            </Stack>
            <Stack gap={0}>
              <Typography variant="h5" fontWeight={700} color="anBlue.main">
                {demographics.firstName} {demographics.lastName}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {age} years
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {demographics.gender}
              </Typography>
            </Stack>
          </Stack>
          <Stack>
            <Stack
              direction="row"
              alignItems="flex-end"
              gap={1}
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" color="contrast.dark">
                DOB:
              </Typography>
              <Typography variant="body2">
                {moment(demographics.dob).format('DD/MM/YYYY')}
              </Typography>
            </Stack>

            {(!isIrishRegion(tenantInfo?.region) || demographics?.nhsNumber) && (
              <>
                <Divider variant="middle" />

                <Stack
                  direction="row"
                  alignItems="flex-end"
                  gap={1}
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2" color="contrast.dark">
                    NHS No.:
                  </Typography>
                  <Typography variant="body2">
                    {demographics.nhsNumber || '—'}
                  </Typography>
                </Stack>
              </>
            )}
            <Divider variant="middle" />

            <Stack
              direction="row"
              alignItems="flex-end"
              gap={1}
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" color="contrast.dark">
                {orgSettings?.orgShortName || orgSettings?.orgName || ''}{' '}
                Patient ID:
              </Typography>
              <Typography variant="body2">
                {patient.patientID || '—'}
              </Typography>
            </Stack>
            {(demographics?.hospitalNumbers || []).map((x, i) => (
              <div key={i}>
                <Divider variant="middle" />

                <Stack
                  direction="row"
                  alignItems="flex-end"
                  gap={1}
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2" color="contrast.dark">
                    {x?.organisation?.orgName}:
                  </Typography>
                  <Typography variant="body2">{x?.mrn || '—'}</Typography>
                </Stack>
              </div>
            ))}
          </Stack>
        </Stack>

        {categorisedRisks?.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {categorisedRisks.flatMap((cat) =>
              cat.flags.map((flag, i) => (
                <Chip
                  key={`${cat.group}-${flag}`}
                  label={flag}
                  sx={{
                    backgroundColor: `${cat.color}15`,
                    border: `1px solid ${cat.color}40`,
                    color: cat.color,
                    fontWeight: 500,
                    borderRadius: '16px',
                  }}
                />
              )),
            )}
          </Stack>
        ) : null}
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {/* DEMOGRAPHICS CARD */}
        <Grid item xs={12} md={6} lg={4} display="flex">
          <InfoCard title="Demographics" grow>
            <Info label="GP Surgery" value={demographics.gpSurgery} />
            <Info
              label="Address"
              value={[
                demographics.addressLine1,
                demographics.addressLine2,
                demographics.town,
                demographics.city,
                demographics.postCode,
              ]
                .filter(Boolean)
                .join(', ')}
            />
            <Info label="Email" value={demographics.email} />
            <Info label="Phone" value={demographics.phoneNumber1} />
            <Info label="Alt Phone" value={demographics.phoneNumber2} />
          </InfoCard>
        </Grid>

        {/* NEXT OF KIN CARD — equal height */}
        <Grid item xs={12} md={6} lg={4} display="flex">
          <InfoCard title="Next of Kin" grow>
            <Info label="Name" value={demographics.nokName} />
            <Info label="Relationship" value={demographics.nokRelationship} />
            <Info label="Contact" value={demographics.nokContactNumber} />
          </InfoCard>
        </Grid>

        {/* MOBILITY CARD */}
        <Grid item xs={12} md={6} lg={4}>
          <InfoCard title="Mobility">
            <Stack
              direction="column"
              spacing={1.2}
              sx={{
                '& > div': {
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                  pb: 0.5,
                },
              }}
            >
              <Info label="Category" value={mobility.category} />
              <Info
                label="Height"
                value={mobility.heightCm ? `${mobility.heightCm} cm` : undefined}
              />
              <Info
                label="Weight"
                value={mobility.weightKg ? `${mobility.weightKg} kg` : undefined}
              />
              <Info
                label="Bariatric"
                value={mobility.isBariatric ? 'Yes' : 'No'}
              />
              <Info
                label="Must Travel Alone"
                value={mobility.mustTravelAlone ? 'Yes' : 'No'}
              />
              <Info
                label="Aids"
                value={(mobility.aids || []).join(', ') || 'None'}
              />
            </Stack>
          </InfoCard>
        </Grid>

        {/* CLINICAL NOTES */}
        {clinicalNotes && (
          <Grid item xs={12} md={6} lg={12}>
            <InfoCard title="Clinical Notes">
              <Stack gap={1}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {clinicalNotes}
                </Typography>
                <DetailRow label="Allergies" value={allergies.join(', ')} />
                <DetailRow label="Medications" value={medications.join(', ')} />
                <DetailRow
                  label="PMHx"
                  value={pmhx.map((x) => x.disease).join(', ')}
                />
              </Stack>
            </InfoCard>
          </Grid>
        )}

        {/* JOURNEYS */}
        <Grid item xs={12}>
          <RiskAssessments
            patient={patient}
            orgName={orgSettings?.orgName || orgSettings?.orgShortName}
            orgLogo={orgSettings?.logo}
          />
        </Grid>

        <Grid item xs={12}>
          <ExistingJourneys patientID={patient?._id} />
        </Grid>

        {/* LOGS */}
        <Grid item xs={12}>
          <InfoCard>
            <LogsComponent
              title="Log"
              targetId={patient._id}
              targetType="ptsPatient"
              logsLoadedAt={logsLoadedAt}
            />
          </InfoCard>
        </Grid>
      </Grid>
      <AddNewPatient
        open={editMode}
        onClose={() => {
          setEditMode(false)
        }}
        existingPatient={patient}
        actionAfterSubmit={(patientResponse) => {
          setPatient(patientResponse)
          setEditMode(false)
          setLogsLoadedAt(moment().toDate())
        }}
      />
    </Box>
  )
}

const Info = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    sx={{
      borderBottom: '1px solid rgba(0,0,0,0.04)',
      pb: 0.5,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={500}
      textAlign="right"
      sx={{ maxWidth: '65%', wordBreak: 'break-word' }}
    >
      {value || '—'}
    </Typography>
  </Stack>
)

export default PTSPatient
